import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../../services/api';

const STATUS_COLORS: Record<string, string> = {
  Present: '#16a34a',
  Late: '#d97706',
  'On OT': '#2563eb',
  Absent: '#dc2626',
};

export default function TmsDashboard() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/tms/dashboard')
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-gray-600">Loading dashboard...</div>;
  if (!data) return <div className="p-6 text-gray-500">Dashboard data unavailable.</div>;

  const statusData = [
    { name: 'Present', value: data.statusBreakdownToday.present },
    { name: 'Late', value: data.statusBreakdownToday.late },
    { name: 'On OT', value: data.statusBreakdownToday.onOt },
    { name: 'Absent', value: data.statusBreakdownToday.absent },
  ];
  const hasStatusData = statusData.some((s) => s.value > 0);

  const incidentBars = (rows: any[]) =>
    rows.map((r) => ({ name: r.employee_code, incidents: Number(r.incident_count) }));

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Time Management Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-700 mb-1">Scheduled Employees Today</h3>
          <p className="text-3xl font-bold text-blue-600">{data.scheduledTodayCount}</p>
          <p className="text-sm text-gray-500">Employees with a shift assigned for today</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-700 mb-2">Today's Attendance Status</h3>
          {hasStatusData ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  {statusData.map((s) => (
                    <Cell key={s.name} fill={STATUS_COLORS[s.name]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 py-8 text-center">No attendance transactions recorded for today yet.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-700 mb-2">Frequently Late (last 30 days)</h3>
          {data.frequentLate.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No lateness incidents in the last 30 days</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={incidentBars(data.frequentLate)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={70} />
                <Tooltip />
                <Bar dataKey="incidents" fill="#d97706" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-700 mb-2">Frequently Early-Out (last 30 days)</h3>
          {data.frequentEarlyOut.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No early-out incidents in the last 30 days</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={incidentBars(data.frequentEarlyOut)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={70} />
                <Tooltip />
                <Bar dataKey="incidents" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-700 mb-2">Frequently on OT (last 30 days)</h3>
          {data.frequentOt.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No OT incidents in the last 30 days</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={incidentBars(data.frequentOt)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={70} />
                <Tooltip />
                <Bar dataKey="incidents" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
